import { useQuery } from "@tanstack/solid-query";
import type { Component } from "solid-js";
import { createEffect, createResource, Match, on, Show, Switch } from "solid-js";
import { Portal } from "solid-js/web";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import {
	PremiumProductBlocV1,
	PremiumProductBlocV2
} from "~/solidJs/entities/PremiumPopUp/ui/PremiumProductBloc";
import { CloseBtnV2 } from "~/solidJs/entities/SolidModalView/ui/CloseBtn";
import { ProductsApi } from "~/solidJs/shared/api/ProductsApi";
import { isUserFromRussia, useCustomContext } from "~/solidJs/shared/helpers";
import { SuspenseBlock } from "~/solidJs/shared/ui";
import {
	ViewPortRoot,
	ViewportElement
} from "~/solidJs/shared/ui/molecules/BreakPointObserver/BreakPointObserver";
import { UnsubscribeQuestionsPage } from "~/solidJs/widgets/UnsubscribeQuestionsWidget/UnsubscribeQuestionsWidget";
import { getUserStore, isPremiumModalShown, setIsPremiumModalShown } from "../../HeaderMain/models";
import { PricingFeaturesTableEntity } from "../../PricingFeaturesTableEntity/PricingFeaturesTableEntity";
import { PricingFeaturesTableEntityV2 } from "../../PricingFeaturesTableEntity/PricingFeaturesTableEntityV2";
import { PricingFeedBackLogoSection } from "../../PricingFeedBackEntity/PricingFeedBackEntity";
import { LayoutSolidModalView } from "../../SolidModalView";
import { createpremiumPopUpStore, PremiumPopUpContext } from "../models/premiumPopUpContext";
import { CreativeTeamsSection } from "./CreativeTeamsSection";
import { Testimonials } from "./Testimonials";

type LayoutPremiumPopUp = Record<string, never>;

const shouldOpenRuCards = () => {
	const user = getUserStore();
	const subscription = user?.subscription;
	if (subscription === "stripe") {
		return false;
	}
	return isUserFromRussia(user) || user?.subscription === "yookassa";
};

export const LayoutPremiumPopUp: Component<LayoutPremiumPopUp> = (_props) => {
	const premiumPopUpStore = createpremiumPopUpStore(shouldOpenRuCards());
	createEffect(
		on(
			isPremiumModalShown,
			(isPremiumModalShown) => {
				EventAnalytics.premiumBannerOpenClose(
					!!isPremiumModalShown,
					isPremiumModalShown ? isPremiumModalShown.source : undefined
				);
				if (!isPremiumModalShown) return;
				premiumPopUpStore.setPremiumPopUpStore((prev) => ({
					...prev,
					source: isPremiumModalShown.source,
					isUnsubscribeQuestionsVisible: isPremiumModalShown.unsubscribe || false,
					promocodeChange: isPremiumModalShown?.promocode,
					isRuCards: isPremiumModalShown?.isRuCards ?? prev.isRuCards
				}));
			},
			{ defer: true }
		)
	);
	return (
		<Show when={isPremiumModalShown()}>
			<Portal mount={document.body}>
				<PremiumPopUpContext.Provider value={premiumPopUpStore}>
					<PopUpV3 />
				</PremiumPopUpContext.Provider>
			</Portal>
		</Show>
	);
};

const _PopUpV2 = () => {
	const tariffs = useQuery(() => ({
		queryKey: ["premium-pop-up"],
		queryFn: () => ProductsApi.getProductList()
	}));
	const tariffsFeatures = useQuery(() => ({
		queryKey: ["premium-pop-up-features"],
		queryFn: () => ProductsApi.getPricingTableDataGrouped()
	}));
	const premiumPopUpStore = useCustomContext(PremiumPopUpContext);
	let scrollContainer: HTMLElement;
	return (
		<ViewPortRoot>
			<LayoutSolidModalView class="!pt-20 !pb-0 !px-20" toggleSelf={setIsPremiumModalShown}>
				<div class="relative h-full w-full">
					<CloseBtnV2
						onClick={() => setIsPremiumModalShown(false)}
						class="-right-2 absolute top-0 translate-x-full"
					/>
					<article
						data-test={"premium-pop-up"}
						class={
							"relative flex h-full w-full flex-col items-center justify-start gap-32 overflow-y-auto overflow-x-visible rounded-t-[32px] bg-white px-14 py-8"
						}
						ref={scrollContainer!}
						onclick={(e) => e.stopPropagation()}
					>
						<Switch>
							<Match
								when={
									premiumPopUpStore.getPremiumPopUpStore()
										.isUnsubscribeQuestionsVisible
								}
							>
								<UnsubscribeQuestionsPage
									setIsVisible={(isVisible) =>
										premiumPopUpStore.setPremiumPopUpStore((prev) => ({
											...prev,
											isUnsubscribeQuestionsVisible: isVisible
										}))
									}
								/>
							</Match>
							<Match
								when={
									!premiumPopUpStore.getPremiumPopUpStore()
										.isUnsubscribeQuestionsVisible
								}
							>
								<SuspenseBlock>
									<Show when={tariffs.data} keyed>
										{(items) => (
											<PremiumProductBlocV2
												forceSelectedPlan="plus"
												itemsToRender={items}
											/>
										)}
									</Show>

									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"pricing-table"
											)
										}
									>
										<Show when={tariffsFeatures.data && tariffs.data} keyed>
											{(features) => (
												<PricingFeaturesTableEntityV2
													selectedPriceId={"plus"}
													tariffsResFeatures={tariffsFeatures.data}
													tariffsRes={features}
												/>
											)}
										</Show>
									</ViewportElement>

									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"creative-teams"
											)
										}
									>
										<CreativeTeamsSection />
									</ViewportElement>
									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"pricing-feedback-logo"
											)
										}
									>
										<PricingFeedBackLogoSection />
									</ViewportElement>
								</SuspenseBlock>
							</Match>
						</Switch>
					</article>
				</div>
			</LayoutSolidModalView>
		</ViewPortRoot>
	);
};

const PopUpV3 = () => {
	const tariffs = useQuery(() => ({
		queryKey: ["premium-pop-up"],
		queryFn: () => ProductsApi.getProductList()
	}));
	const tariffsFeatures = useQuery(() => ({
		queryKey: ["premium-pop-up-features"],
		queryFn: () => ProductsApi.getPricingTableDataGrouped()
	}));
	const premiumPopUpStore = useCustomContext(PremiumPopUpContext);
	let scrollContainer: HTMLElement;
	return (
		<ViewPortRoot>
			<LayoutSolidModalView class="!pt-20 !pb-0 !px-20" toggleSelf={setIsPremiumModalShown}>
				<div class="relative h-full w-full">
					<CloseBtnV2
						onClick={() => setIsPremiumModalShown(false)}
						class="-right-2 absolute top-0 translate-x-full"
					/>
					<article
						data-test={"premium-pop-up"}
						class={
							"relative flex h-full w-full flex-col items-center justify-start gap-32 overflow-y-auto overflow-x-visible rounded-t-[32px] bg-white px-14 py-8"
						}
						ref={scrollContainer!}
						onclick={(e) => e.stopPropagation()}
					>
						<Switch>
							<Match
								when={
									premiumPopUpStore.getPremiumPopUpStore()
										.isUnsubscribeQuestionsVisible
								}
							>
								<UnsubscribeQuestionsPage
									setIsVisible={(isVisible) =>
										premiumPopUpStore.setPremiumPopUpStore((prev) => ({
											...prev,
											isUnsubscribeQuestionsVisible: isVisible
										}))
									}
								/>
							</Match>
							<Match
								when={
									!premiumPopUpStore.getPremiumPopUpStore()
										.isUnsubscribeQuestionsVisible
								}
							>
								<SuspenseBlock>
									<Show when={tariffs.data} keyed>
										{(items) => (
											<PremiumProductBlocV2
												forceSelectedPlan="plus"
												itemsToRender={items}
											/>
										)}
									</Show>

									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"pricing-table"
											)
										}
									>
										<Show when={tariffsFeatures.data && tariffs.data} keyed>
											{(features) => (
												<PricingFeaturesTableEntityV2
													selectedPriceId={"plus"}
													tariffsResFeatures={tariffsFeatures.data}
													tariffsRes={features}
												/>
											)}
										</Show>
									</ViewportElement>

									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"creative-teams"
											)
										}
									>
										<CreativeTeamsSection />
									</ViewportElement>
									<ViewportElement
										class="w-full"
										onEnterViewport={() =>
											EventAnalytics.premiumBannerScrollTarget(
												"pricing-feedback-logo"
											)
										}
									>
										<PricingFeedBackLogoSection />
									</ViewportElement>
								</SuspenseBlock>
							</Match>
						</Switch>
					</article>
				</div>
			</LayoutSolidModalView>
		</ViewPortRoot>
	);
};

const _PopUpV1 = () => {
	const [tariffsRes] = createResource(ProductsApi.getProductList);
	const [tariffsFeaturesRes] = createResource(ProductsApi.getPricingTableData);
	const premiumPopUpStore = useCustomContext(PremiumPopUpContext);
	let scrollContainer: HTMLElement;
	return (
		<ViewPortRoot>
			<LayoutSolidModalView autoCloseInit toggleSelf={setIsPremiumModalShown}>
				<article
					data-test={"premium-pop-up"}
					class={
						"relative flex h-full w-full flex-col items-center justify-start gap-10 overflow-y-auto overflow-x-hidden rounded-xl bg-white px-4 py-2"
					}
					ref={scrollContainer!}
					onclick={(e) => e.stopPropagation()}
				>
					<Switch>
						<Match
							when={
								premiumPopUpStore.getPremiumPopUpStore()
									.isUnsubscribeQuestionsVisible
							}
						>
							<UnsubscribeQuestionsPage
								setIsVisible={(isVisible) =>
									premiumPopUpStore.setPremiumPopUpStore((prev) => ({
										...prev,
										isUnsubscribeQuestionsVisible: isVisible
									}))
								}
							/>
						</Match>
						<Match
							when={
								!premiumPopUpStore.getPremiumPopUpStore()
									.isUnsubscribeQuestionsVisible
							}
						>
							<SuspenseBlock>
								<Show when={tariffsRes()} keyed>
									{(items) => (
										<PremiumProductBlocV1
											forceSelectedPlan="plus"
											itemsToRender={items}
											scrollContainerRef={scrollContainer}
										/>
									)}
								</Show>
							</SuspenseBlock>
							<ViewportElement
								class="w-full"
								onEnterViewport={() =>
									EventAnalytics.premiumBannerScrollTarget("pricing-table")
								}
							>
								<Show when={tariffsFeaturesRes()} keyed>
									{(features) => (
										<PricingFeaturesTableEntity tariffsResFeatures={features} />
									)}
								</Show>
							</ViewportElement>

							<ViewportElement
								class="w-full"
								onEnterViewport={() =>
									EventAnalytics.premiumBannerScrollTarget("testimonials")
								}
							>
								<Testimonials />
							</ViewportElement>
							<ViewportElement
								class="w-full"
								onEnterViewport={() =>
									EventAnalytics.premiumBannerScrollTarget("creative-teams")
								}
							>
								<CreativeTeamsSection />
							</ViewportElement>
							<ViewportElement
								class="w-full"
								onEnterViewport={() =>
									EventAnalytics.premiumBannerScrollTarget(
										"pricing-feedback-logo"
									)
								}
							>
								<PricingFeedBackLogoSection />
							</ViewportElement>
						</Match>
					</Switch>
				</article>
			</LayoutSolidModalView>
		</ViewPortRoot>
	);
};
